import { toQueryFromOptions } from '@sparkpost/report-builder/helpers/metrics';
import React, { FormEvent, forwardRef, useEffect, useMemo } from 'react';
import { RadioButtonGroup } from 'src/components';
import {
  Box,
  Button,
  Column,
  Columns,
  ComboBoxTextField,
  Drawer,
  Panel,
  ScreenReaderOnly,
  Stack
} from 'src/components/matchbox';
import { Comparison, Uppercase } from 'src/components/text';
import { Form } from 'src/components/tracking';
import { toSelectOptions } from 'src/helpers/array';
import { allMetricsAreDeliverability } from 'src/helpers/metrics';
import { UseAnalyticsReportContext } from 'src/typescript';
import { FILTER_OPTIONS } from '../../constants';
import { useAnalyticsReportContext } from '../../context/AnalyticsReportContext';
import {
  filterTypeExistsInGroupings,
  toApiFormattedGroupings,
  toGroupingFields
} from '../../helpers';
import useAllowSubjectCampaignFilter from '../../hooks/useAllowSubjectCampaignFilter';
import { SubjectCampaignsWarning } from '../index';
import Typeahead from '../Typeahead';
import {
  AddButton,
  CompareBySelect,
  DuplicateErrorBanner,
  MultiEntryController,
  RemoveButton,
  TypeSelect
} from './components';
import useFiltersForm from './useFiltersForm';

const FILTER_VALUE_PLACEHOLDER_TEXT = 'e.g. resource_01 or resource_02';

type FiltersFormProps = {
  handleSubmit: (value: $TODOFIXME) => void;
};

// eslint-disable-next-line react/display-name
const FiltersForm = forwardRef<HTMLFormElement, FiltersFormProps>(({ handleSubmit }, ref) => {
  const { state, actions } = useFiltersForm();
  const { status } = state;
  const {
    setGroupingType,
    setFilterType,
    setFilterCompareBy,
    setFilterValues,
    addGrouping,
    addFilter,
    removeFilter,
    clearFilters,
    setFilters,
    submit
  } = actions;
  const groupings = toGroupingFields(state.groupings);
  const { state: reportOptions } = useAnalyticsReportContext() as UseAnalyticsReportContext;
  const { metrics, filters, to, from } = reportOptions;

  function handleFormSubmit(e: FormEvent): void {
    e.preventDefault(); // Prevents page refresh
    submit();
  }

  useEffect(() => {
    if (status === 'error') {
      // Using refs to manage focus is a *huge* pain with multiple elements - this isn't pretty but gets the job done and eliminates a *lot* of complexity
      // that comes with using a ref callback to store refs in an array.
      const errorBanners: NodeListOf<HTMLElement> = document.querySelectorAll(
        '[data-id="error-banner"]'
      );

      errorBanners[0].focus();
    }

    // If the status changes to "success" (only possible via form submission) then call the passed in submit handler
    if (status === 'success') {
      handleSubmit({ filters: toApiFormattedGroupings(groupings) });
    }

    // For this effect, only care about the status changing, so not using an exhaustive dependency check here
    // eslint-disable-next-line
  }, [status]);

  useEffect(() => {
    setFilters(filters);
  }, [setFilters, filters]);

  const FILTER_TYPES = useAllowSubjectCampaignFilter()
    ? FILTER_OPTIONS
    : FILTER_OPTIONS.filter(({ value }) => value !== 'subject_campaigns');

  // Removes extraneous values from the options before passing them to a `<select />` as options
  const filterTypeOptions = useMemo(() => toSelectOptions(FILTER_TYPES), [FILTER_TYPES]);

  const { to: formattedTo, from: formattedFrom } = toQueryFromOptions({ to, from } as $TODOFIXME);

  const MAX_FILTERS_ITEMS_TO_COMPARE = 10;

  const shouldShowSubjectCampaignWarning =
    !allMetricsAreDeliverability(metrics) &&
    filterTypeExistsInGroupings(groupings, 'subject_campaigns');

  const helpTextByFilter = (filterType: string) => {
    const baseHelpText = (entity: string) =>
      `A maximum of ${MAX_FILTERS_ITEMS_TO_COMPARE} ${entity} can be added.`;

    const helpTextMapByFilterType = {
      domains: 'recipient domains',
      sending_ips: 'sending IPs',
      ip_pools: 'IP pools',
      campaigns: 'campaign IDs',
      mailbox_providers: 'mailbox providers',
      mailbox_provider_regions: 'mailbox provider regions',
      templates: 'templates',
      sending_domains: 'sending domains',
      subaccounts: 'subaccounts'
    };

    const helpTextSpecific = helpTextMapByFilterType[filterType];

    if (helpTextSpecific) return baseHelpText(helpTextSpecific);

    return undefined;
  };

  return (
    <Form onSubmit={handleFormSubmit} id="analyticsreport-filter-form" ref={ref}>
      <Box padding="500" paddingBottom="8rem">
        {shouldShowSubjectCampaignWarning && (
          <Box marginY="400">
            <SubjectCampaignsWarning type="filter" />
          </Box>
        )}

        <Stack>
          {groupings.map((grouping, groupingIndex) => {
            return (
              <div key={`grouping-${groupingIndex}`}>
                <Stack>
                  <Panel data-id="grouping">
                    {grouping.filters.map((filter, filterIndex) => {
                      const filterConfig = FILTER_TYPES.find((item) => item.value === filter.type); // Find the matching configuration entry
                      const filterQuery = filterConfig?.query;
                      const filterLabel = filterConfig?.label;
                      const filterSelector = filterConfig?.selector;
                      const filterValue = filterConfig?.value;

                      return (
                        <Box
                          as={Panel.Section}
                          position="relative"
                          key={`filters-${groupingIndex}-${filterIndex}`}
                        >
                          {status === 'error' &&
                          filterIndex === 0 &&
                          grouping.hasDuplicateFilters ? (
                            <Box marginY="400">
                              <DuplicateErrorBanner data-id="error-banner" />
                            </Box>
                          ) : null}

                          <Box as="fieldset" border="0" padding="0">
                            <ScreenReaderOnly as={'legend' as $TODOFIXME}>
                              Filter By
                            </ScreenReaderOnly>
                            <Stack space="400">
                              <Columns alignY="bottom">
                                <Column>
                                  <TypeSelect
                                    onChange={(e: $TODOFIXME) =>
                                      setFilterType({
                                        filterType: e.target.value,
                                        groupingIndex,
                                        filterIndex
                                      })
                                    }
                                    id={`filter-by-${groupingIndex}-${filterIndex}`}
                                    value={filterValue || undefined}
                                    options={filterTypeOptions}
                                  />
                                </Column>

                                <Column>
                                  {filter.hasCompareBySelect ? (
                                    <CompareBySelect
                                      onChange={(e: $TODOFIXME) =>
                                        setFilterCompareBy({
                                          compareBy: e.target.value,
                                          groupingIndex,
                                          filterIndex
                                        })
                                      }
                                      id={`compare-by-${groupingIndex}-${filterIndex}`}
                                      hasLikeOptions={filter.hasCompareByLikeOptions}
                                      value={filter.compareBy}
                                    />
                                  ) : null}
                                </Column>
                              </Columns>

                              {filter.valueField === 'typeahead' ? (
                                <Typeahead
                                  id={`typeahead-${groupingIndex}-${filterIndex}`}
                                  lookaheadRequest={filterQuery}
                                  lookaheadOptions={{ to: formattedTo, from: formattedFrom }}
                                  selector={filterSelector}
                                  itemToString={(item: $TODOFIXME) =>
                                    item?.value ? item.value : ''
                                  }
                                  groupingIndex={groupingIndex}
                                  filterType={filter.type}
                                  filterIndex={filterIndex}
                                  setFilterValues={setFilterValues}
                                  value={filter.values}
                                  type={filterLabel}
                                  label={filterLabel}
                                  placeholder={FILTER_VALUE_PLACEHOLDER_TEXT}
                                  data-track={true}
                                  maxItems={MAX_FILTERS_ITEMS_TO_COMPARE}
                                  helpText={helpTextByFilter(filter.type)}
                                />
                              ) : null}

                              {filter.valueField === 'multi-entry' ? (
                                <MultiEntryController
                                  id={`multi-entry-${groupingIndex}-${filterIndex}`}
                                  initialValueList={filter.values}
                                  setFilterValues={setFilterValues}
                                  filterIndex={filterIndex}
                                  groupingIndex={groupingIndex}
                                  render={(props: $TODOFIXME) => {
                                    return (
                                      <ComboBoxTextField
                                        label={filterLabel}
                                        id={props.id}
                                        removeItem={props.handleRemove}
                                        onBlur={props.handleBlur}
                                        onKeyDown={props.handleKeyDown}
                                        onChange={props.handleChange}
                                        value={props.value}
                                        error={props.error}
                                        selectedItems={props.valueList}
                                        itemToString={(item) => (item ? item : '')}
                                        placeholder={FILTER_VALUE_PLACEHOLDER_TEXT}
                                        data-track={true}
                                        maxItems={MAX_FILTERS_ITEMS_TO_COMPARE}
                                        helpText={helpTextByFilter(filter.type)}
                                      />
                                    );
                                  }}
                                />
                              ) : null}

                              {filter.hasGroupingTypeRadioGroup ? (
                                <RadioButtonGroup label="Grouping Type">
                                  <RadioButtonGroup.Button
                                    name={`grouping-type-${groupingIndex}-${filterIndex}`}
                                    id={`radio-button-and-${groupingIndex}-${filterIndex}`}
                                    checked={grouping.type === 'AND'}
                                    value="AND"
                                    data-track={true}
                                    onChange={() => {
                                      if (grouping.type === 'OR') {
                                        setGroupingType({
                                          groupingType: 'AND',
                                          groupingIndex
                                        });
                                      }
                                    }}
                                  >
                                    <Uppercase>And</Uppercase>
                                  </RadioButtonGroup.Button>

                                  <RadioButtonGroup.Button
                                    name={`grouping-type-${groupingIndex}-${filterIndex}`}
                                    id={`radio-button-or-${groupingIndex}-${filterIndex}`}
                                    checked={grouping.type === 'OR'}
                                    value="OR"
                                    data-track={true}
                                    onChange={() => {
                                      if (grouping.type === 'AND') {
                                        setGroupingType({
                                          groupingIndex,
                                          groupingType: 'OR'
                                        });
                                      }
                                    }}
                                  >
                                    <Uppercase>Or</Uppercase>
                                  </RadioButtonGroup.Button>
                                </RadioButtonGroup>
                              ) : null}

                              {filter.hasAndButton ? (
                                <Box>
                                  <AddButton onClick={() => addFilter({ groupingIndex })}>
                                    <ScreenReaderOnly>Add </ScreenReaderOnly>
                                    <Uppercase>And</Uppercase>
                                    <ScreenReaderOnly>Filter</ScreenReaderOnly>
                                  </AddButton>
                                </Box>
                              ) : null}

                              {filter.hasOrButton ? (
                                <Box>
                                  <AddButton onClick={() => addFilter({ groupingIndex })}>
                                    <ScreenReaderOnly>Add </ScreenReaderOnly>
                                    <Uppercase>Or</Uppercase>
                                    <ScreenReaderOnly> Filter</ScreenReaderOnly>
                                  </AddButton>
                                </Box>
                              ) : null}

                              {filter.hasComparisonBetweenFilters && grouping.type ? (
                                <Comparison>{grouping.type}</Comparison>
                              ) : null}
                            </Stack>

                            {filter.hasRemoveButton ? (
                              <RemoveButton
                                onClick={() => removeFilter({ filterIndex, groupingIndex })}
                              />
                            ) : null}
                          </Box>
                        </Box>
                      );
                    })}
                  </Panel>

                  {grouping.hasAndBetweenGroups ? <Comparison>And</Comparison> : null}
                </Stack>

                {grouping.hasAndButton ? (
                  <Box marginBottom="800" marginTop="500">
                    <AddButton onClick={() => addGrouping()}>
                      <ScreenReaderOnly>Add</ScreenReaderOnly>
                      <Uppercase>And</Uppercase>
                      <ScreenReaderOnly>Grouping</ScreenReaderOnly>
                    </AddButton>
                  </Box>
                ) : null}
              </div>
            );
          })}
        </Stack>

        <Drawer.Footer>
          <Box display="flex">
            <Box pr="100" flex="1">
              <Button useMatchboxVariant={false} width="100%" variant="primary" type="submit">
                Apply Filters
              </Button>
            </Box>
            <Box pl="100" flex="1">
              <Button
                useMatchboxVariant={false}
                width="100%"
                variant="secondary"
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        </Drawer.Footer>
      </Box>
    </Form>
  );
});

export default FiltersForm;
